import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import {
  WORLD_EXPLORER_IDS,
  getWorldExplorer,
} from '../components/worldExplorer';
import { getPathLessons as getPathLessonIds } from '../utils/skillGraph';
import { getGuidedLesson, hasGuidedLesson } from '../utils/guidedLessonRegistry';
import { isLessonAccessible } from '../config/accessConfig';
import { usePremium, isPremiumMockEnabled } from '../utils/premiumStore';
import { trackLearning } from '../utils/analytics';

const pageStyle = {
  minHeight: '100vh',
  background: 'var(--color-bg)',
  padding: '20px 18px 48px',
  maxWidth: 960,
  margin: '0 auto',
  boxSizing: 'border-box',
};

const backLinkStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 15,
  fontWeight: 600,
  color: 'var(--color-primary)',
  textDecoration: 'none',
  marginBottom: 20,
};

const titleStyle = {
  fontSize: 'clamp(1.45rem, 4vw, 1.85rem)',
  fontWeight: 800,
  color: 'var(--color-text)',
  margin: '0 0 10px',
  letterSpacing: '-0.02em',
};

const descStyle = {
  fontSize: 16,
  lineHeight: 1.5,
  color: 'var(--color-text)',
  opacity: 0.78,
  maxWidth: 640,
  marginBottom: 22,
};

const searchRowStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
  alignItems: 'center',
  marginBottom: 22,
};

const inputStyle = {
  flex: '1 1 220px',
  minWidth: 0,
  maxWidth: 420,
  padding: '12px 14px',
  borderRadius: 12,
  border: '1px solid var(--color-border)',
  background: 'var(--color-surface, var(--color-bg))',
  color: 'var(--color-text)',
  fontSize: 15,
};

const chipRowStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
};

const chipBtn = (active) => ({
  padding: '8px 14px',
  borderRadius: 999,
  border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'}`,
  background: active ? 'rgba(99, 102, 241, 0.15)' : 'var(--color-surface, var(--color-bg))',
  color: 'var(--color-text)',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
});

const worldSectionStyle = {
  marginBottom: 28,
  padding: '18px 18px 20px',
  borderRadius: 18,
  border: '1px solid var(--color-border)',
  background: 'var(--color-surface, var(--color-bg))',
  boxShadow: '0 4px 18px var(--color-shadow)',
};

const lessonRowStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'baseline',
  gap: '8px 14px',
  padding: '10px 0',
  borderBottom: '1px solid var(--color-border)',
};

const lessonRowLast = {
  ...lessonRowStyle,
  borderBottom: 'none',
};

const lessonLinkStyle = {
  fontWeight: 700,
  color: 'var(--color-primary)',
  textDecoration: 'none',
  fontSize: 15,
};

const mutedStyle = {
  fontSize: 14,
  color: 'var(--color-text)',
  opacity: 0.55,
};

const metaStyle = {
  fontSize: 13,
  color: 'var(--color-text)',
  opacity: 0.65,
  whiteSpace: 'nowrap',
};

const badgeStyle = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  padding: '3px 8px',
  borderRadius: 6,
  background: 'rgba(245, 158, 11, 0.2)',
  color: 'var(--color-text)',
};

const activityWrapStyle = {
  width: '100%',
  flexBasis: '100%',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  marginTop: 2,
};

const activityTagStyle = {
  fontSize: 12,
  padding: '3px 8px',
  borderRadius: 8,
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  color: 'var(--color-text)',
  opacity: 0.85,
};

const pathsLineStyle = {
  fontSize: 13,
  color: 'var(--color-text)',
  opacity: 0.62,
  lineHeight: 1.4,
  width: '100%',
  flexBasis: '100%',
  marginTop: 4,
};

const worldLessonListStyle = {
  marginTop: 16,
  listStyle: 'none',
  margin: 0,
  padding: 0,
};

const srOnly = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

function titleForLesson(worldId, lessonId) {
  const g = getGuidedLesson(worldId, lessonId);
  if (g?.title) return g.title;
  return lessonId
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function activityStepsForLesson(lesson) {
  if (!lesson?.sequence) return [];
  return lesson.sequence.filter((s) => s.type === 'activity' || s.type === 'miniChallenge');
}

function matchesQuery(q, parts) {
  if (!q) return true;
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return parts.some((p) => String(p).toLowerCase().includes(needle));
}

/** Path titles that include this lesson, in explorer path order. */
function pathTitlesForLesson(worldId, lessonId) {
  const world = getWorldExplorer(worldId);
  const titles = [];
  for (const path of world?.learningPaths || []) {
    try {
      if (getPathLessonIds(worldId, path.id).includes(lessonId)) titles.push(path.title);
    } catch {
      /* ignore */
    }
  }
  return titles;
}

/**
 * Unique lesson ids per world, stable order: first time a lesson appears when walking
 * learning paths in UI order (matches how paths reuse lessons in content).
 */
function orderedUniqueLessonIds(worldId) {
  const world = getWorldExplorer(worldId);
  const seen = new Set();
  const order = [];
  for (const path of world?.learningPaths || []) {
    let ids = [];
    try {
      ids = getPathLessonIds(worldId, path.id);
    } catch {
      ids = [];
    }
    for (const lid of ids) {
      if (seen.has(lid)) continue;
      seen.add(lid);
      order.push(lid);
    }
  }
  return order;
}

export default function LessonCatalogPage() {
  const { locale } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [premiumActive] = usePremium();

  const topicsBase = locale ? `/${locale}/topics` : '/topics';
  const worldFilter = searchParams.get('world') || 'all';

  useEffect(() => {
    trackLearning.lessonCatalogOpen();
  }, []);

  const catalogRows = useMemo(() => {
    const rows = [];
    for (const worldId of WORLD_EXPLORER_IDS) {
      if (worldFilter !== 'all' && worldFilter !== worldId) continue;
      const world = getWorldExplorer(worldId);
      if (!world) continue;
      for (const lessonId of orderedUniqueLessonIds(worldId)) {
        const title = titleForLesson(worldId, lessonId);
        const guided = getGuidedLesson(worldId, lessonId);
        const activities = activityStepsForLesson(guided);
        const pathTitles = pathTitlesForLesson(worldId, lessonId);
        rows.push({
          worldId,
          worldTitle: world.title,
          lessonId,
          pathTitles,
          title,
          guided: !!guided,
          minutes: guided?.estimatedMinutes ?? null,
          activities,
          accessible:
            hasGuidedLesson(worldId, lessonId) &&
            isLessonAccessible(worldId, lessonId, { hasPremium: premiumActive }),
        });
      }
    }
    return rows;
  }, [worldFilter, premiumActive]);

  const filteredRows = useMemo(() => {
    const q = query.trim();
    return catalogRows.filter((r) =>
      matchesQuery(q, [
        r.title,
        r.worldTitle,
        r.lessonId,
        ...r.pathTitles,
        ...r.activities.map((a) => a.title || a.stepId || ''),
        ...r.activities.map((a) => a.activityType || ''),
      ]),
    );
  }, [catalogRows, query]);

  const setWorldChip = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id === 'all') next.delete('world');
    else next.set('world', id);
    setSearchParams(next, { replace: true });
  };

  return (
    <div style={pageStyle}>
      <Link to={`${topicsBase}/explorer`} style={backLinkStyle}>
        <ChevronLeft size={22} aria-hidden />
        World Explorer
      </Link>

      <h1 style={titleStyle}>Lesson index</h1>
      <p style={descStyle}>
        Each guided lesson is listed once per world. When a lesson is part of more than one learning
        path, every path name is shown underneath so nothing looks duplicated by mistake. Open a
        lesson to run it step by step; activity names below are a quick preview of what is inside.
      </p>

      <div style={searchRowStyle}>
        <label htmlFor="lesson-catalog-search" style={srOnly}>
          Search lessons
        </label>
        <input
          id="lesson-catalog-search"
          type="search"
          placeholder="Search lessons, paths, worlds, or activities…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={inputStyle}
          autoComplete="off"
        />
      </div>

      <div style={chipRowStyle} role="group" aria-label="Filter by world">
        <button type="button" style={chipBtn(worldFilter === 'all')} onClick={() => setWorldChip('all')}>
          Every world
        </button>
        {WORLD_EXPLORER_IDS.map((id) => {
          const w = getWorldExplorer(id);
          if (!w) return null;
          return (
            <button
              key={id}
              type="button"
              style={chipBtn(worldFilter === id)}
              onClick={() => setWorldChip(id)}
            >
              {w.icon ? <span aria-hidden>{w.icon} </span> : null}
              {w.title}
            </button>
          );
        })}
      </div>

      {filteredRows.length === 0 ? (
        <p style={{ ...mutedStyle, marginTop: 28 }}>No lessons match that search. Try a shorter word or clear the filter.</p>
      ) : null}

      {WORLD_EXPLORER_IDS.map((worldId) => {
        if (worldFilter !== 'all' && worldFilter !== worldId) return null;
        const world = getWorldExplorer(worldId);
        if (!world) return null;
        const worldRows = filteredRows.filter((r) => r.worldId === worldId);
        if (worldRows.length === 0) return null;

        return (
          <section key={worldId} style={{ ...worldSectionStyle, background: world.bannerGradient }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
              <h2 style={{ ...titleStyle, fontSize: '1.35rem', margin: 0 }}>
                <span aria-hidden>{world.icon} </span>
                {world.title}
              </h2>
              <Link
                to={`${topicsBase}/${worldId}`}
                style={{ ...lessonLinkStyle, fontSize: 14 }}
              >
                Open world paths →
              </Link>
            </div>

            <p style={{ ...mutedStyle, marginTop: 10, marginBottom: 0 }}>
              {worldRows.length} lesson{worldRows.length === 1 ? '' : 's'} — paths named under a lesson show where it appears in the app.
            </p>

            <ul style={worldLessonListStyle}>
              {worldRows.map((r, i) => {
                const lessonUrl = `${topicsBase}/${worldId}/lesson/${r.lessonId}`;
                const isLast = i === worldRows.length - 1;
                const rowStyle = isLast ? lessonRowLast : lessonRowStyle;
                const showPremiumLock = isPremiumMockEnabled() && r.guided && !r.accessible;
                const pathsLabel =
                  r.pathTitles.length > 0 ? r.pathTitles.join(' · ') : '';

                return (
                  <li key={`${worldId}-${r.lessonId}`} style={rowStyle}>
                    <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                      {r.guided && r.accessible ? (
                        <Link to={lessonUrl} style={lessonLinkStyle}>
                          {r.title}
                        </Link>
                      ) : r.guided && showPremiumLock ? (
                        <Link
                          to={`${topicsBase}/${worldId}`}
                          style={{ ...lessonLinkStyle, opacity: 0.85 }}
                          title="Open this world to unlock with Explore Premium (test mode)."
                        >
                          {r.title}
                        </Link>
                      ) : (
                        <span style={{ fontWeight: 700, color: 'var(--color-text)' }}>{r.title}</span>
                      )}
                      {showPremiumLock ? (
                        <span style={{ ...badgeStyle, marginLeft: 8 }}>Premium</span>
                      ) : null}
                    </div>
                    {r.minutes != null ? <span style={metaStyle}>~{r.minutes} min</span> : null}
                    {pathsLabel ? (
                      <div style={pathsLineStyle}>
                        <span style={{ fontWeight: 600, opacity: 0.75 }}>Paths: </span>
                        {pathsLabel}
                      </div>
                    ) : null}
                    {r.activities.length > 0 ? (
                      <div style={activityWrapStyle}>
                        {r.activities.slice(0, 6).map((a) => (
                          <span key={a.stepId || a.title} style={activityTagStyle} title={a.activityType || a.type}>
                            {a.title || a.stepId || 'Activity'}
                          </span>
                        ))}
                        {r.activities.length > 6 ? (
                          <span style={{ ...activityTagStyle, opacity: 0.7 }}>
                            +{r.activities.length - 6} more
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
